import React, { SetStateAction, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Campaign } from '../../../../model/campaign/Campaign';
import { IOffer } from '../../../../model/offer/IOffer';
import { IOfferItems } from '../../../../model/offer/IOfferItems';
import { User } from '../../../../model/user/User';
import { CustomAutocomplete } from '../../../../shared/components/Autocomplete';
import CustomDatePicker from '../../../../shared/components/DatePicker';
import {
    calculateUploadUntil,
    formatDate,
    isSelectedDateValid,
    removeTimeZoneFromDate,
} from '../../../../shared/functions/Functions';
import usePagination from '../../../../shared/hooks/usePagination';
import { plannerInfluencer } from '../../../../store/influencer/plannerSlice';
import { IAllStates } from '../../../../store/rootReducer';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../utils/toasters';
import { iManageList } from '../../../brand/campaign/manage/calendar/dto/IManageCalendar';
import { InfluencerPlannerService } from '../service';
import moment from 'moment';

export interface INewEvent {
    postingDate: string | null;
    caption: string | null;
    offerItemId: number;
    acceptedUser?: User;
    contentCreatorId?: number;
    campaignId?: number;
    campaign?: Campaign;
}

interface ICreateEventModalProps {
    createEvent: INewEvent;
    setCreateEvent: React.Dispatch<SetStateAction<INewEvent>>;
    setSelectedEvent?: React.Dispatch<SetStateAction<iManageList>>;
    setIsEditModalOpened?: React.Dispatch<SetStateAction<boolean>>;
    isCreateModalOpened: boolean;
    isDisabled?: boolean;
    handleCloseModal: () => void;
}

const CreateEventModal = ({
    createEvent,
    setCreateEvent,
    isCreateModalOpened,
    setIsEditModalOpened,
    setSelectedEvent,
    handleCloseModal,
    isDisabled = false,
}: ICreateEventModalProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { events } = useSelector((reduxState: IAllStates) => reduxState.plannerInfluencer);
    const { selectedCampaign: selectedFilterCampaign } = useSelector((state: IAllStates) => state.plannerInfluencer);

    const { filterParams, setFilterParams } = usePagination({});
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<Campaign>();
    const [selectedOffer, setSelectedOffer] = useState<IOffer>();
    const isOfferSelected = !!selectedOffer?.offerItems?.length;
    // The contract has an amendment waiting for a signature, its postings cannot be scheduled until it is signed
    const hasOpenAnnex = !!selectedOffer?.hasOpenAnnex;
    const [selectedPlatform, setSelectedPlatform] = useState<IOfferItems>();

    const dataArrPostingType =
        selectedOffer?.offerItems
            ?.filter(offer => offer.nbPosts > (offer?.eventNbPosts ?? 0))
            ?.sort((a, b) => (b.socialPlatform.name > a.socialPlatform.name ? 1 : -1)) ?? [];

    const handleChangeTimepicker = (newValue: Date | null, name: string) => {
        setCreateEvent(prev => ({ ...prev, [name]: newValue }));
    };

    const numReviewsDays = selectedCampaign?.isReviewed ? selectedCampaign?.reviewDays : 0;

    const changeCampaignHandler = (paramKey: string, paramValue: string | string[] | number) => {
        const selected = campaigns?.find(campaign => campaign?.offers?.[0]?.id === +paramValue);
        setSelectedOffer(selected?.offers?.[0]);
        setSelectedCampaign(selected);
    };

    const changeOfferHandler = (paramKey: string, paramValue: string | string[] | number) => {
        if (paramKey === 'offerItemId' && paramValue === '') {
            setSelectedPlatform(undefined);
        }
        setCreateEvent(prev => ({ ...prev, [paramKey]: +paramValue }));
    };

    const handleCreateEvent = async () => {
        if (!selectedCampaign?.id) {
            return WarningToast(t('influencer.planner.chose_campaign'));
        }
        if (createEvent.offerItemId === 0) {
            WarningToast(t('influencer.planner.chose_platform'));
        } else {
            await InfluencerPlannerService.createNewEvent(createEvent)
                .then(response => {
                    dispatch(plannerInfluencer.actions.setEvents(events?.concat(response)));
                    setCreateEvent({
                        caption: null,
                        postingDate: moment
                            .utc()
                            .set({
                                hour: 5,
                                minute: 0,
                                second: 0,
                                millisecond: 0,
                            })
                            .toISOString(),
                        offerItemId: 0,
                    });
                    setSelectedCampaign(undefined);
                    setSelectedOffer(undefined);
                    SuccessToast(t('influencer.planner.successCreated'));
                    getPlannerCampaigns();
                    handleCloseModal();
                    dispatch(plannerInfluencer.actions.setUpdateApi());
                    if (setSelectedEvent && setIsEditModalOpened) {
                        const data = InfluencerPlannerService.getPlannerModalData(response?.[0]?.id);
                        data?.then(res => {
                            setSelectedEvent(res);
                            setIsEditModalOpened(prev => !prev);
                        });
                    }
                })
                .catch(error => ErrorToast(error));
        }
    };

    const getPlannerCampaigns = async () => {
        await InfluencerPlannerService.getPlannerData({ ...filterParams, perPage: 0 })
            .then(response => {
                setCampaigns(
                    response.data?.map((campaign: Campaign) => ({
                        ...campaign,
                        offerId: campaign?.offers?.[0]?.id,
                    }))
                );
            })
            .catch(error => ErrorToast(error));
    };

    useEffect(() => {
        getPlannerCampaigns();
    }, [JSON.stringify(filterParams)]);

    useEffect(() => {
        if (Number(createEvent?.offerItemId) && selectedOffer) {
            setSelectedPlatform(selectedOffer?.offerItems?.find(offer => offer?.id === +createEvent?.offerItemId));
        }
    }, [createEvent?.offerItemId, selectedOffer]);

    useEffect(() => {
        if (createEvent?.campaign?.id) {
            setSelectedCampaign(createEvent?.campaign);
            const currentOffer = campaigns?.find(campaign =>
                campaign?.offers?.[0].offerItems?.find(offer => offer.id === createEvent?.offerItemId)
            );
            setSelectedOffer(currentOffer?.offers?.[0]);
        }
    }, [createEvent?.campaign, campaigns?.length]);

    useEffect(() => {
        if (selectedFilterCampaign?.id && !!campaigns.length) {
            changeCampaignHandler('campaignId', selectedFilterCampaign?.id);
        }
    }, [selectedFilterCampaign?.id, campaigns?.length]);

    return (
        <Modal
            show={isCreateModalOpened}
            centered
            onHide={() => {
                handleCloseModal();
                setSelectedOffer(undefined);
                setSelectedCampaign(undefined);
            }}
            keyboard={false}
        >
            <Modal.Body>
                <div className="border-bottom mb-3">
                    <h5>{t('influencer.planner.schedulePosting')}</h5>
                </div>
                {hasOpenAnnex && (
                    <div className="annex-description-card mb-3">
                        <h6 className="mb-1">{t('brand.campaign.book.contract.modal.annex.spending_blocked_title')}</h6>
                        <p className="mb-0">
                            {t('brand.campaign.book.contract.modal.annex.schedule_blocked_campaign')}
                        </p>
                    </div>
                )}
                <div className="my-3">
                    <CustomAutocomplete
                        dataArray={campaigns}
                        isWithImage={false}
                        inputLabel="title"
                        isLongSelected={selectedOffer?.isLongTerm}
                        defaultArray={
                            campaigns?.filter(campaign => campaign?.id === selectedCampaign?.id)[0] as unknown as []
                        }
                        // defaultArray={createEvent?.campaign as unknown as Campaign[]}
                        key={selectedCampaign?.id}
                        setFilterState={setFilterParams}
                        isDisabled={isDisabled}
                        changeEventKey="title"
                        queryFilterKey="offerId"
                        inputLabelTranslation={t('influencer.planner.selectCampaign')}
                        handleChangeParams={changeCampaignHandler}
                    />
                </div>
                {isOfferSelected && (
                    <>
                        <div className="my-3">
                            <CustomAutocomplete
                                dataArray={dataArrPostingType}
                                isWithImage={true}
                                inputLabel="socialPlatform.name"
                                isDisabled={isDisabled}
                                photoLabel="socialPlatform.path"
                                defaultArray={
                                    selectedOffer?.offerItems?.find(
                                        offer => offer.id === Number(createEvent?.offerItemId)
                                    ) as unknown as IOfferItems[]
                                }
                                selectedStateImage={selectedPlatform?.socialPlatform?.path as string}
                                queryFilterKey="offerItemId"
                                key={selectedOffer?.id}
                                inputLabelTranslation={t('brand.campaign.manage.postingType')}
                                handleChangeParams={changeOfferHandler}
                            />
                            {selectedPlatform?.id && (
                                <span className="text-warning d-flex mt-2 justify-content-end">
                                    Posts {selectedPlatform?.eventNbPosts ?? 0} / {selectedPlatform?.nbPosts}
                                </span>
                            )}
                            {!dataArrPostingType.length && (
                                <p className="text-danger">{t('influencer.planner.error_posting_type')}</p>
                            )}
                        </div>
                        <div className="my-3">
                            <CustomDatePicker
                                label={t('brand.campaign.manage.postingDate')}
                                value={createEvent?.postingDate}
                                disableFrom={removeTimeZoneFromDate(selectedCampaign?.publishFrom ?? '')}
                                disableTo={removeTimeZoneFromDate(selectedCampaign?.publishTo ?? '')}
                                onChange={(event: Date | null) => handleChangeTimepicker(event, 'postingDate')}
                            />
                            {!isSelectedDateValid(
                                createEvent?.postingDate ?? '',
                                selectedCampaign?.publishFrom,
                                selectedCampaign?.publishTo
                            ) && (
                                <p className="text-danger">
                                    {t('influencer.planner.notInRange')}
                                    {formatDate(selectedCampaign?.publishFrom as string)} -{' '}
                                    {formatDate(selectedCampaign?.publishTo as string)}
                                </p>
                            )}
                        </div>
                        <p>
                            {t('influencer.planner.uploadContentUntil')}:
                            {calculateUploadUntil(numReviewsDays, createEvent?.postingDate as string)}
                        </p>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="outline-primary"
                    onClick={() => {
                        handleCloseModal();
                        setSelectedOffer(undefined);
                        setSelectedCampaign(undefined);
                    }}
                >
                    {t('general.button.cancel')}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleCreateEvent}
                    disabled={
                        hasOpenAnnex ||
                        !isSelectedDateValid(
                            createEvent?.postingDate ?? '',
                            selectedCampaign?.publishFrom,
                            selectedCampaign?.publishTo
                        )
                    }
                >
                    {t('general.button.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateEventModal;
